import React, { useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { designTokens } from '@/utils/design-tokens'

interface MonthYearSelectorProps {
  isOpen: boolean
  onClose: () => void
  onRangeSelect: (startDate: Date, endDate: Date) => void
  currentYear?: number
  currentMonth?: number
}

const MonthYearSelector: React.FC<MonthYearSelectorProps> = ({
  isOpen,
  onClose,
  onRangeSelect,
  currentYear = new Date().getFullYear()
}) => {
  const [selectedYear, setSelectedYear] = useState(currentYear)
  const [firstSelection, setFirstSelection] = useState<{ year: number; month: number } | null>(null)
  const [hoveredMonth, setHoveredMonth] = useState<{ year: number; month: number } | null>(null)

  const monthNames = [
    '1월', '2월', '3월', '4월', '5월', '6월',
    '7월', '8월', '9월', '10월', '11월', '12월'
  ]

  if (!isOpen) return null

  const handleMonthClick = (monthIndex: number) => {
    if (!firstSelection) {
      // First click - store the selection
      setFirstSelection({ year: selectedYear, month: monthIndex })
    } else {
      // Second click - create date range and close
      const firstDate = new Date(firstSelection.year, firstSelection.month, 1)
      const secondDate = new Date(selectedYear, monthIndex, 1)

      let startDate: Date
      let endDate: Date

      if (firstDate <= secondDate) {
        startDate = firstDate
        // Get last day of the selected end month
        endDate = new Date(selectedYear, monthIndex + 1, 0)
      } else {
        startDate = secondDate
        // Get last day of the first selected month
        endDate = new Date(firstSelection.year, firstSelection.month + 1, 0)
      }

      onRangeSelect(startDate, endDate)
      setFirstSelection(null)
      onClose()
    }
  }

  const handlePrevYear = () => {
    setSelectedYear(prev => prev - 1)
  }

  const handleNextYear = () => {
    setSelectedYear(prev => prev + 1)
  }

  const getMonthStyle = (monthIndex: number): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
      padding: '8px 12px',
      borderRadius: '4px',
      cursor: 'pointer',
      transition: 'all 0.15s ease',
      fontFamily: designTokens.typography.fontFamily.sans[0],
      fontSize: designTokens.typography.fontSize['body-small'][0],
      fontWeight: 400
    }

    // Check if this month is in selection range
    if (firstSelection) {
      const currentMonthDate = new Date(selectedYear, monthIndex, 1)
      const firstSelectionDate = new Date(firstSelection.year, firstSelection.month, 1)

      // Highlight first selection
      if (selectedYear === firstSelection.year && monthIndex === firstSelection.month) {
        return {
          ...baseStyle,
          backgroundColor: designTokens.colors.primary.DEFAULT,
          color: designTokens.colors.white,
          fontWeight: 600
        }
      }

      // Highlight range when hovering
      if (hoveredMonth) {
        const hoveredDate = new Date(hoveredMonth.year, hoveredMonth.month, 1)
        const minDate = firstSelectionDate <= hoveredDate ? firstSelectionDate : hoveredDate
        const maxDate = firstSelectionDate <= hoveredDate ? hoveredDate : firstSelectionDate

        if (currentMonthDate >= minDate && currentMonthDate <= maxDate) {
          return {
            ...baseStyle,
            backgroundColor: `${designTokens.colors.primary.DEFAULT}20`,
            color: designTokens.colors.primary.dark
          }
        }
      }
    }

    return {
      ...baseStyle,
      backgroundColor: 'transparent',
      color: designTokens.colors.gray[6]
    }
  }

  return (
    <div
      style={{
        position: 'absolute',
        top: '40px',
        left: '0',
        backgroundColor: designTokens.colors.white,
        border: `1px solid ${designTokens.colors.gray[2]}`,
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        zIndex: 1000,
        padding: '16px',
        minWidth: '320px'
      }}
    >
      {/* Year Navigation */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={handlePrevYear}
          className="flex items-center justify-center w-8 h-8 rounded hover:bg-gray-50 transition-colors"
          style={{ color: designTokens.colors.gray[5] }}
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        <h3
          style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: designTokens.typography.fontSize['body-medium-bold'][0],
            fontWeight: 600,
            color: designTokens.colors.gray[6]
          }}
        >
          {selectedYear}년
        </h3>

        <button
          onClick={handleNextYear}
          className="flex items-center justify-center w-8 h-8 rounded hover:bg-gray-50 transition-colors"
          style={{ color: designTokens.colors.gray[5] }}
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Month Grid */}
      <div className="grid grid-cols-4 gap-2">
        {monthNames.map((month, index) => (
          <div
            key={index}
            style={getMonthStyle(index)}
            onClick={() => handleMonthClick(index)}
            onMouseEnter={() => {
              if (firstSelection) {
                setHoveredMonth({ year: selectedYear, month: index })
              }
            }}
            onMouseLeave={() => setHoveredMonth(null)}
            className="text-center hover:bg-gray-50"
          >
            {month}
          </div>
        ))}
      </div>

      {/* Instructions */}
      <div
        className="mt-4 pt-3 text-center"
        style={{
          borderTop: `1px solid ${designTokens.colors.gray[1]}`,
          fontFamily: designTokens.typography.fontFamily.sans[0],
          fontSize: designTokens.typography.fontSize['body-xsmall'][0],
          color: firstSelection ? designTokens.colors.primary.DEFAULT : designTokens.colors.gray[4],
          fontWeight: firstSelection ? 600 : 400
        }}
      >
        {firstSelection
          ? `시작: ${firstSelection.year}년 ${firstSelection.month + 1}월 | 종료 월을 선택하세요`
          : '시작 월을 선택하세요 (2번 클릭으로 기간 설정)'}
      </div>

      {/* Close button overlay */}
      <div
        className="fixed inset-0"
        style={{ zIndex: -1 }}
        onClick={onClose}
      />
    </div>
  )
}

export default MonthYearSelector